import React, { useState, useEffect, useCallback, memo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Typography, Grid, Chip, Box, Button, Skeleton, Container, IconButton, Tooltip, CircularProgress, useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { Helmet } from 'react-helmet-async';
import CreateIcon from '@mui/icons-material/Create';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Expandable from '../components/common/Expandable';
import { getAnimeDetails, getAnimeStatus, getAnimeCharacters, getAnimeStaffs, AnimeStatusResponse } from '../services/animeApi';
import { AnimeDetail, AnimeRelation, GroupedCharacter, GroupedStaff, AnimeVideo, AnimeReviewData, AnimeTmdbImage } from '../types/Anime';
import AnimeGallerySection from '../components/image/AnimeGallerySection';
import AnimeScoresSection from '../components/AnimeScoresSection';
import YoutubeViewer from '../components/video/YoutubeViewer';
import AnimeStatusButton from '../components/AnimeStatusButton';
import AnimeReviewDialog from '../components/AnimeReviewDialog';
import WikipediaViewer from '../components/WikipediaViewer';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import ExpandableChipList from '../components/common/ExpandableChipList';
import { useUser } from '../contexts/UserContext';

// ============================================================
// Styled Components (GameDetailsPage準拠)
// ============================================================

const FullWidthGlassSection = styled('div')({
  position: 'relative',
  width: '100vw',
  left: '50%',
  right: '50%',
  marginTop: '-240px',
  marginLeft: '-50vw',
  marginRight: '-50vw',
  paddingTop: '64px',
  zIndex: 1,
  '@media (max-width: 899px)': {
    marginTop: '-164px',
    paddingTop: '56px',
  }
});

const BackgroundImage = styled('div')<{ image: string }>(({ image }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundImage: `url(${image})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  filter: 'blur(35px)',
  opacity: 0.1,
  zIndex: -1,
}));

const GlassOverlay = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(18, 18, 18, 0)',
  zIndex: -1,
});

const ContentWrapper = styled(Container)({
  position: 'relative',
  zIndex: 1,
  overflow: 'visible',
  '@media (max-width: 899px)': {
    paddingTop: '0 !important'
  }
});

const NormalSection = styled('div')({
  position: 'relative',
  zIndex: 2,
  backgroundColor: '#121212',
});

const HeroImageSection = styled('div')({
  position: 'relative',
  width: '100%',
  height: '464px',
  marginTop: '-64px',
  marginBottom: '-100px',
  overflow: 'hidden',
  '@media (max-width: 899px)': {
    height: '400px',
    marginTop: '-56px',
    marginBottom: '-60px',
  }
});

const HeroGradient = styled('div')({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: '200px',
  background: 'linear-gradient(to bottom, rgba(18, 18, 18, 0) 0%, rgba(18, 18, 18, 0.85) 70%, rgba(18, 18, 18, 1) 100%)',
  pointerEvents: 'none',
});

const HeroFallbackGradient = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'linear-gradient(135deg, #1a237e 0%, #121212 100%)',
  opacity: 0.5,
});

const HeroBackdrop = styled('div')<{ image: string }>(({ image }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundImage: `url(${image})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center 25%',
  opacity: 0.6,
}));

const HeroBlurredCover = styled('div')<{ image: string }>(({ image }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundImage: `url(${image})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  filter: 'blur(25px)',
  opacity: 0.35,
}));

const CoverImageSection = styled('div')({
  position: 'relative',
  width: '100%',
  maxWidth: '400px',
  marginBottom: '0.5rem',
  '@media (max-width: 899px)': {
    width: '35%',
    minWidth: '120px',
    maxWidth: '120px',
    marginTop: '-80px',
    marginLeft: '0px',
    zIndex: 2,
    display: 'flex',
    flexDirection: 'column',
  }
});

const CoverImage = styled('img')({
  width: '100%',
  height: 'auto',
  display: 'block',
  borderRadius: '4px',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
  '@media (max-width: 899px)': {
    aspectRatio: '3/4',
    objectFit: 'cover',
  }
});

const StatusButtonsContainer = styled(Box)({
  marginTop: '8px',
  '@media (max-width: 899px)': {
    position: 'absolute',
    right: '-120px',
    bottom: '0',
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    width: '110px'
  }
});

const LoginPrompt = styled(Link)({
  fontSize: '0.7rem',
  color: 'rgba(255, 255, 255, 0.7)',
  marginTop: '8px',
  textAlign: 'center',
  display: 'block',
  textDecoration: 'none',
  '&:hover': {
    color: 'rgba(255, 255, 255, 1)',
  }
});

// ============================================================
// Helper Components
// ============================================================

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="h6" component="h2" sx={{
    fontSize: { xs: '0.75rem', sm: '0.8rem' },
    fontWeight: 600,
    mb: 1,
    color: '#ffffff',
  }}>
    {children}
  </Typography>
);

const MetaChip: React.FC<{ label: string }> = ({ label }) => (
  <Chip
    label={label}
    size="small"
    sx={{
      fontSize: { xs: '0.6rem', sm: '0.65rem' },
      height: { xs: '20px', sm: '22px' },
      borderRadius: '4px',
      bgcolor: 'rgba(255, 255, 255, 0.08)',
      color: '#ffffffcc',
    }}
  />
);

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
    <Typography variant="caption" sx={{ color: '#a7a7a7', minWidth: '60px', fontSize: '0.65rem' }}>
      {label}
    </Typography>
    <Typography variant="caption" sx={{ color: '#ffffffcc', fontSize: '0.65rem' }}>
      {value}
    </Typography>
  </Box>
);

const getScoreColor = (score: number) => {
  if (score >= 8.5) return '#4CAF50';
  if (score >= 7.5) return '#8BC34A';
  if (score >= 6.5) return '#CDDC39';
  if (score >= 5.5) return '#FFC107';
  if (score >= 4.5) return '#FF9800';
  return '#F44336';
};

const getStatusLabel = (key: string) => {
  const map: Record<string, string> = {
    s1: '視聴済み', s2: '視聴中', s3: '中断', s4: '視聴中止', s5: 'ウォッチリスト'
  };
  return map[key] || key;
};

const relationLabelMap: Record<string, string> = {
  'Sequel': '続編',
  'Prequel': '前作',
  'Side Story': 'サイドストーリー',
  'Summary': '総集編',
  'Full Story': '完全版',
  'Parent Story': '親ストーリー',
  'Alternative Setting': '別設定',
  'Alternative Version': '別バージョン',
  'Spin-Off': 'スピンオフ',
  'Adaptation': '原作/アダプテーション',
  'Character': 'キャラクター',
  'Other': 'その他',
};

const seasonLabelMap: Record<string, string> = {
  'spring': '春', 'summer': '夏', 'fall': '秋', 'winter': '冬'
};

const statusLabelMap: Record<string, string> = {
  'Finished Airing': '放送終了',
  'Currently Airing': '放送中',
  'Not yet aired': '放送前',
};

const sourceLabelMap: Record<string, string> = {
  'Manga': '漫画',
  'Light novel': 'ライトノベル',
  'Visual novel': 'ビジュアルノベル',
  'Novel': '小説',
  'Original': 'オリジナル',
  'Game': 'ゲーム',
  'Web manga': 'Web漫画',
  'Web novel': 'Web小説',
  'Music': '音楽',
  'Book': '書籍',
  '4-koma manga': '4コマ漫画',
  'Card game': 'カードゲーム',
  'Mixed media': 'ミクストメディア',
  'Picture book': '絵本',
  'Radio': 'ラジオ',
  'Other': 'その他',
};

const formatDuration = (duration: string): string => {
  const match = duration.match(/(?:(\d+)\s*hr\s*)?(\d+)\s*min/);
  if (!match) return duration;
  const [, hours, minutes] = match;
  if (hours) return `${hours}h${minutes}min`;
  return `${minutes}min`;
};

// ============================================================
// Main Component
// ============================================================

const AnimeDetailsPage: React.FC = () => {
  const { mal_id } = useParams<{ mal_id: string }>();
  const navigate = useNavigate();
  const { user, isLoading: userLoading } = useUser();
  const [anime, setAnime] = useState<AnimeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [userStatus, setUserStatus] = useState<number | null>(null);
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  const [wikiSummary, setWikiSummary] = useState<string | null>(null);
  const [wikiOpen, setWikiOpen] = useState(false);
  const [hasWikiData, setHasWikiData] = useState(false);
  const [reviewData, setReviewData] = useState<AnimeReviewData>({
    score: null, reviewText: null, scoreTags: [],
    scoreStory: null, scoreAnimation: null, scoreSound: null,
    scoreCharacter: null, scoreDirection: null,
    episodesWatched: null, watchMedium: [], watchStyle: [],
    startDate: null, endDate: null, rewatch: false,
  });

  // 初期表示件数 (バックエンドのpage_sizeとは独立)
  // キャラ: 2行分 → xs=4cols(8), sm=5cols(10), md=6cols(12)
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));
  const INITIAL_CHARS_DISPLAY = isMd ? 12 : isSm ? 10 : 8;
  const INITIAL_STAFF_DISPLAY = 9;

  // 遅延取得用 state
  const [extraCharacters, setExtraCharacters] = useState<GroupedCharacter[]>([]);
  const [charPage, setCharPage] = useState(2);
  const [charHasMore, setCharHasMore] = useState(false);
  const [charLoading, setCharLoading] = useState(false);
  const [charExpanded, setCharExpanded] = useState(false);

  const [extraStaff, setExtraStaff] = useState<GroupedStaff[]>([]);
  const [staffPage, setStaffPage] = useState(2);
  const [staffHasMore, setStaffHasMore] = useState(false);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffExpanded, setStaffExpanded] = useState(false);
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);

  const buildReviewData = (statusData: AnimeStatusResponse): AnimeReviewData => ({
    score: statusData.score,
    reviewText: statusData.review_text,
    scoreTags: statusData.score_tags || [],
    scoreStory: statusData.score_story,
    scoreAnimation: statusData.score_animation,
    scoreSound: statusData.score_sound,
    scoreCharacter: statusData.score_character,
    scoreDirection: statusData.score_direction,
    episodesWatched: statusData.episodes_watched,
    watchMedium: statusData.watch_medium || [],
    watchStyle: statusData.watch_style || [],
    startDate: statusData.start_date,
    endDate: statusData.end_date,
    rewatch: statusData.rewatch,
  });

  useEffect(() => {
    if (userLoading) return;

    const fetchAnimeDetails = async () => {
      setLoading(true);
      setAnime(null);
      setUserStatus(null);
      setExtraCharacters([]);
      setCharPage(2);
      setCharHasMore(false);
      setCharExpanded(false);
      setExtraStaff([]);
      setStaffPage(2);
      setStaffHasMore(false);
      setStaffExpanded(false);
      try {
        if (mal_id) {
          const malIdNum = parseInt(mal_id, 10);
          const data = await getAnimeDetails(malIdNum);
          setAnime(data);
          setCharHasMore(data.character_voice_actors.length < data.character_voice_actors_count);
          setStaffHasMore(data.staff.length < data.staff_count);

          // ログイン中ならユーザーステータス取得
          const token = localStorage.getItem('token');
          if (token) {
            try {
              const statusData = await getAnimeStatus(malIdNum);
              setUserStatus(statusData.status);
              setReviewData(buildReviewData(statusData));
            } catch {}
          }
        }
      } catch (error) {
        console.error('アニメ詳細の取得に失敗しました', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnimeDetails();
    window.scrollTo(0, 0);
  }, [mal_id, user, userLoading]);

  const loadMoreCharacters = useCallback(async () => {
    if (!mal_id || charLoading) return;
    setCharLoading(true);
    try {
      const res = await getAnimeCharacters(parseInt(mal_id, 10), charPage);
      setExtraCharacters(prev => [...prev, ...res.results]);
      setCharPage(prev => prev + 1);
      setCharHasMore(res.next !== null);
    } catch (e) {
      console.error('キャラクター追加取得に失敗', e);
    } finally {
      setCharLoading(false);
    }
  }, [mal_id, charPage, charLoading]);

  const loadMoreStaff = useCallback(async () => {
    if (!mal_id || staffLoading) return;
    setStaffLoading(true);
    try {
      const res = await getAnimeStaffs(parseInt(mal_id, 10), staffPage);
      setExtraStaff(prev => [...prev, ...res.results]);
      setStaffPage(prev => prev + 1);
      setStaffHasMore(res.next !== null);
    } catch (e) {
      console.error('スタッフ追加取得に失敗', e);
    } finally {
      setStaffLoading(false);
    }
  }, [mal_id, staffPage, staffLoading]);

  // Wikipedia概要取得
  useEffect(() => {
    if (anime) {
      const fetchWikiSummary = async () => {
        try {
          const response = await fetch(`https://ja.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(anime.title_japanese || anime.title)}`);
          const data = await response.json();
          if (data.extract) {
            setWikiSummary(data.extract);
            setHasWikiData(true);
          } else {
            setHasWikiData(false);
          }
        } catch (error) {
          console.error('Wikipedia APIからのデータ取得に失敗しました:', error);
          setHasWikiData(false);
        }
      };
      fetchWikiSummary();
    }
  }, [anime]);

  // ============================================================
  // Access Guard
  // ============================================================


  // ============================================================
  // Loading
  // ============================================================
  if (loading) {
    return (
      <>
        <HeroImageSection>
          <HeroFallbackGradient />
          <HeroGradient />
        </HeroImageSection>
        <FullWidthGlassSection>
          <ContentWrapper maxWidth="lg" sx={{ px: 1.5, pt: { xs: 2, md: 4 }, pb: 4 }}>
            <Grid container spacing={{ xs: 2, md: 4 }}>
              <Grid item xs={12} md={3.5}>
                <CoverImageSection>
                  <Skeleton variant="rectangular" sx={{ width: '100%', height: 0, paddingBottom: '133.33%', borderRadius: '4px' }} />
                </CoverImageSection>
              </Grid>
              <Grid item xs={12} md={8.5}>
                <Skeleton width="80%" height={32} />
                <Skeleton width="60%" height={24} sx={{ mt: 1 }} />
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Skeleton width={60} height={24} />
                  <Skeleton width={60} height={24} />
                  <Skeleton width={60} height={24} />
                </Box>
                <Skeleton width="100%" height={100} sx={{ mt: 2 }} />
              </Grid>
            </Grid>
          </ContentWrapper>
        </FullWidthGlassSection>
        <NormalSection>
          <Container maxWidth="lg" sx={{ px: 1.5, pt: 2, pb: 4, minHeight: '50vh' }}>
            <Skeleton width="100%" height={200} />
          </Container>
        </NormalSection>
      </>
    );
  }

  if (!anime) {
    return (
      <Container maxWidth="lg" sx={{ pt: 10, textAlign: 'center' }}>
        <Typography>アニメ情報が見つかりません。</Typography>
      </Container>
    );
  }

  // ============================================================
  // Data
  // ============================================================
  const displayTitle = anime.title_japanese || anime.title;
  const subTitle = anime.title_japanese ? (anime.title_english || anime.title) : anime.title_english;
  const scoreNum = anime.score ? Number(anime.score) : null;
  const imageUrl = anime.image_url
    ? anime.image_url.replace(/(\.\w+)$/, 'l$1')
    : '/image/no_image.png';

  // TMDB backdrop (hero背景用)
  const firstBackdrop = anime.tmdb_images?.find(i => i.image_type === 'backdrop');
  const backdropUrl = firstBackdrop
    ? `https://image.tmdb.org/t/p/w1280${firstBackdrop.file_path}`
    : null;

  const formatAiredDate = () => {
    if (!anime.aired_from) return '不明';
    const from = new Date(anime.aired_from).toLocaleDateString('ja-JP');
    if (!anime.aired_to) return anime.status === 'Currently Airing' ? `${from} - 放送中` : from;
    return `${from} - ${new Date(anime.aired_to).toLocaleDateString('ja-JP')}`;
  };

  // キャラ&声優 (バックエンドでグループ化済み)
  const allCharacters = [...anime.character_voice_actors, ...extraCharacters];
  const displayCharacters = charExpanded ? allCharacters : allCharacters.slice(0, INITIAL_CHARS_DISPLAY);
  const charHasHidden = !charExpanded && allCharacters.length > INITIAL_CHARS_DISPLAY;

  // スタッフ (初回 + 追加分をマージ)
  const allStaffData = [...anime.staff, ...extraStaff];
  const allStaff = staffExpanded ? allStaffData : allStaffData.slice(0, INITIAL_STAFF_DISPLAY);
  const staffHasHidden = !staffExpanded && allStaffData.length > INITIAL_STAFF_DISPLAY;

  // 動画 (promo → music_video順, English版除外)
  const isExcludedVideo = (title?: string | null) => title ? /english|(\bUSA\b)|madman|artist ver|eng sub|viz media|aniplexus/i.test(title) : false;
  const allVideos = [
    ...anime.videos.filter(v => v.video_type === 'promo' && !isExcludedVideo(v.title)),
    ...anime.videos.filter(v => v.video_type === 'music_video' && !isExcludedVideo(v.title)),
  ].slice(0, 4);

  return (
    <>
      <Helmet>
        <title>
          {anime.title_japanese
            ? `${anime.title_japanese} / ${anime.title}${anime.year ? ` (${anime.year})` : ''} | PULSE`
            : `${anime.title}${anime.year ? ` (${anime.year})` : ''} | PULSE`
          }
        </title>
      </Helmet>

      {/* ============================================================ */}
      {/* Hero Section */}
      {/* ============================================================ */}
      <HeroImageSection>
        {backdropUrl ? (
          <>
            <HeroBackdrop image={backdropUrl} />
            <HeroGradient />
          </>
        ) : anime.image_url ? (
          <>
            <HeroBlurredCover image={anime.image_url} />
            <HeroGradient />
          </>
        ) : (
          <>
            <HeroFallbackGradient />
            <HeroGradient />
          </>
        )}
      </HeroImageSection>

      {/* ============================================================ */}
      {/* Glass Section - Main Info */}
      {/* ============================================================ */}
      <FullWidthGlassSection>
        <BackgroundImage image={imageUrl} />
        <GlassOverlay />
        <ContentWrapper maxWidth="lg" sx={{
          px: 1.5,
          pt: { xs: 2, md: 4 },
          pb: { xs: 2, md: 4 }
        }}>
          <Grid container spacing={{ xs: 2, md: 4 }} sx={{
            '@media (max-width: 899px)': { flexDirection: 'column' }
          }}>
            {/* Left: Cover Image */}
            <Grid item xs={12} md={3.5} sx={{
              '@media (max-width: 899px)': { flex: '0 0 auto', maxWidth: '100%', paddingTop: 0 }
            }}>
              <CoverImageSection>
                <CoverImage src={imageUrl} alt={displayTitle} />
                <StatusButtonsContainer sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  '& > *': { flexShrink: 0 },
                  '@media (max-width: 899px)': {
                    marginBottom: !user ? '48px' : 0
                  }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <AnimeStatusButton
                      malId={anime.mal_id}
                      initialStatus={userStatus}
                      onStatusChange={(newStatus) => setUserStatus(newStatus)}
                    />
                    {userStatus && [1, 2, 3].includes(userStatus) && (
                      <Tooltip title="レビューを書く" arrow>
                        <IconButton
                          onClick={() => setOpenReviewDialog(true)}
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            backdropFilter: 'blur(4px)',
                            border: '1px solid rgba(255, 255, 255, 0.08)',
                            borderRadius: '12px',
                            padding: { xs: '6px 12px', sm: '8px 12px' },
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.08)',
                              transform: 'translateY(-1px)',
                            },
                          }}
                        >
                          <CreateIcon fontSize="small" sx={{ color: 'primary.main' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </StatusButtonsContainer>
                {!user && (
                  <LoginPrompt to="/login">
                    ログインしてアニメを記録・管理しよう
                  </LoginPrompt>
                )}
              </CoverImageSection>
            </Grid>

            {/* Right: Metadata */}
            <Grid item xs={12} md={6.85} sx={{
              '@media (max-width: 899px)': { paddingTop: 0 }
            }}>
              {/* Title */}
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '0.9rem', sm: '1.3rem' } }}>
                {displayTitle}
                {anime.year && (
                  <Typography component="span" color="text.secondary" sx={{ ml: 1.5, fontSize: '1em' }}>
                    ({anime.year})
                  </Typography>
                )}
              </Typography>
              {subTitle && subTitle !== displayTitle && (
                <Typography variant="h6" component="h2" sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  lineHeight: 1.2,
                  mb: 1
                }}>
                  {subTitle}
                </Typography>
              )}

              {/* Genre (dot-separated) */}
              {anime.genres.length > 0 && (
                <Typography variant="body2" sx={{
                  color: '#a7a7a7',
                  fontSize: { xs: '0.6rem', sm: '0.7rem' },
                  mb: 1,
                  '& > span::before': { content: '"・"', color: 'rgba(255, 255, 255, 0.3)' }
                }}>
                  {anime.genres.map((g) => (
                    <Box
                      component="span"
                      key={g.genre_id}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { color: '#ffffff' },
                      }}
                      onClick={() => navigate(`/anime/genre/${g.genre_id}`)}
                    >
                      <Link to={`/anime/genre/${g.genre_id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                        {g.name_ja || g.name}
                      </Link>
                    </Box>
                  ))}
                </Typography>
              )}

              {/* Meta Chips */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                {anime.type && <MetaChip label={anime.type === 'Movie' ? '映画' : anime.type} />}
                {anime.episodes && anime.type !== 'Movie' && <MetaChip label={`${anime.episodes}話`} />}
                {anime.source && anime.source !== 'Unknown' && <MetaChip label={sourceLabelMap[anime.source] || anime.source} />}
                {anime.duration && anime.duration !== 'Unknown' && <MetaChip label={formatDuration(anime.duration)} />}
                {anime.rating && <MetaChip label={
                  ({ 'G - All Ages': '全年齢', 'PG - Children': '子供向け', 'PG-13 - Teens 13 or older': 'PG-13', 'R - 17+ (violence & profanity)': 'R17+', 'R+ - Mild Nudity': 'R17+', 'Rx - Hentai': 'R18+' } as Record<string, string>)[anime.rating] || anime.rating
                } />}
                {anime.status && anime.status !== 'Finished Airing' && <MetaChip label={statusLabelMap[anime.status] || anime.status} />}
              </Box>

              {/* Score Display (inline) */}
              {/* {scoreNum && (
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: getScoreColor(scoreNum), lineHeight: 1 }}>
                      {scoreNum.toFixed(2)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: '#a7a7a7' }}>
                      / 10
                    </Typography>
                  </Box>
                  {anime.scored_by && (
                    <Typography sx={{ fontSize: '0.65rem', color: '#a7a7a7' }}>
                      ({anime.scored_by.toLocaleString()}人)
                    </Typography>
                  )}
                  {anime.rank && (
                    <Typography sx={{ fontSize: '0.65rem', color: '#a7a7a7' }}>
                      #{anime.rank}
                    </Typography>
                  )}
                </Box>
              )} */}

              {/* 概要 (summary優先、なければWikipedia) */}
              {(anime.summary || wikiSummary) && (
                <Box sx={{ position: 'relative', mb: 2 }}>
                  <Expandable maxLines={5} expandButtonStyle="icon">
                    <Typography variant="body2" sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      lineHeight: 1.25,
                      margin: 0,
                    }}>
                      {anime.summary || wikiSummary}
                      {hasWikiData && (
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.5)',
                            cursor: 'pointer',
                            ml: 0.5,
                            fontSize: 'inherit',
                            verticalAlign: 'middle',
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                          onClick={() => setWikiOpen(true)}
                        >
                          <AutoStoriesIcon fontSize="small" sx={{ mr: 0.5, fontSize: '0.7rem', verticalAlign: 'middle' }} />
                          Viewerで見る
                        </Typography>
                      )}
                    </Typography>
                  </Expandable>
                </Box>
              )}

              {/* Info Grid */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {/* Studios */}
                    <Typography variant="subtitle1" sx={{ color: '#FFFFFFB3', fontWeight: 400, fontSize: '0.7rem' }}>
                      スタジオ
                    </Typography>
                    {anime.studios.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {anime.studios.map((s) => (
                          <Chip
                            key={s.studio_id}
                            label={s.name_ja || s.name}
                            component={Link}
                            to={`/anime/studio/${s.studio_id}`}
                            clickable
                            size="small"
                            sx={{
                              fontSize: { xs: '0.675rem', sm: '0.7rem' },
                              height: { xs: '20px', sm: '24px' },
                              bgcolor: 'rgba(255, 255, 255, 0.08)',
                              color: '#ffffff',
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ fontSize: '0.675rem', color: '#a7a7a7' }}>情報なし</Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {/* Producers */}
                    <Typography variant="subtitle1" sx={{ color: '#FFFFFFB3', fontWeight: 400, fontSize: '0.7rem' }}>
                      製作
                    </Typography>
                    {anime.producer_details.length > 0 ? (
                      <ExpandableChipList
                        chips={anime.producer_details
                          .filter((p) => p.producer_type !== 'licensor')
                          .map((p) => ({ key: `${p.producer_id}-${p.producer_type}`, label: p.name_ja || p.name, to: `/anime/producer/${p.producer_id}` }))}
                        max={isMd ? 5 : 3}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ fontSize: '0.675rem', color: '#a7a7a7' }}>情報なし</Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {/* Broadcast & Aired Info */}
              <Box sx={{ mt: 2 }}>
                <InfoRow label="放送期間" value={formatAiredDate()} />
                {anime.season && anime.year && (
                  <InfoRow label="シーズン" value={`${anime.year}年 ${seasonLabelMap[anime.season] || anime.season}`} />
                )}
                {anime.broadcast_day && anime.broadcast_time && (
                  <InfoRow label="放送日時" value={`${({ 'Mondays': '月曜', 'Tuesdays': '火曜', 'Wednesdays': '水曜', 'Thursdays': '木曜', 'Fridays': '金曜', 'Saturdays': '土曜', 'Sundays': '日曜' } as Record<string, string>)[anime.broadcast_day] || anime.broadcast_day} ${anime.broadcast_time}`} />
                )}
              </Box>

              {/* External Links */}
              {/* <Box sx={{
                mt: 2,
                display: 'flex',
                gap: { xs: 0.75, sm: 1 },
                '& .MuiButton-root': {
                  minWidth: 'auto',
                  borderRadius: { xs: '6px', sm: '8px' },
                  backgroundColor: 'rgba(255, 255, 255, 0.06)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textTransform: 'none',
                  padding: { xs: '2px 12px', sm: '1px 12px' },
                  color: '#ffffffe8',
                  fontSize: { xs: '0.675rem', sm: '0.7rem' },
                  fontWeight: 500,
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.12)', transform: 'translateY(-1px)' }
                }
              }}>
                <Button variant="contained" size="small" component="a"
                  href={`https://www.amazon.co.jp/s?k=${encodeURIComponent(displayTitle)}&i=instant-video&tag=pulse-db-22`}
                  target="_blank" rel="noopener noreferrer"
                >
                  Amazon
                </Button>
                <Button variant="contained" size="small" component="a"
                  href={`https://www.youtube.com/results?search_query=${encodeURIComponent(displayTitle)}+アニメ+PV`}
                  target="_blank" rel="noopener noreferrer"
                >
                  YouTube
                </Button>
              </Box> */}
            </Grid>
          </Grid>
        </ContentWrapper>
      </FullWidthGlassSection>

      <NormalSection>
        <Container maxWidth="lg" sx={{ px: 1.5, pt: { xs: 3, md: 4 }, pb: 4 }}>
          <Grid container spacing={4}>
            {/* Left Column */}
            <Grid item xs={12} md={8}>

              {/* 概要 (TMDB日本語) */}
              {anime.tmdb_detail && (anime.tmdb_detail.tagline_ja || anime.tmdb_detail.overview_ja) && (
                <Box sx={{ mb: 2 }}>
                  <SectionTitle>概要</SectionTitle>
                  <Expandable maxHeight={250} expandButtonStyle="pill" pillTextAlign="right">
                    {anime.tmdb_detail.tagline_ja && (
                      <Typography variant="body1" component="h3" sx={{ fontSize: '0.85rem', fontStyle: 'italic', color: 'rgba(255,255,255,0.5)', mb: 1.5, lineHeight: 1.25 }}>
                        {anime.tmdb_detail.tagline_ja}
                      </Typography>
                    )}
                    {anime.tmdb_detail.overview_ja && (
                      <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary', fontSize: '0.75rem', lineHeight: 1.35 }}>
                        {anime.tmdb_detail.overview_ja}
                      </Typography>
                    )}
                  </Expandable>
                </Box>
              )}

              {/* Videos */}
              {allVideos.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <SectionTitle>動画</SectionTitle>
                  <Grid container spacing={1.5}>
                    {allVideos.map((video, idx) => {
                      if (!video.youtube_id) return null;
                      return (
                        <Grid item xs={6} sm={6} key={idx}>
                          <Box sx={{ mb: 1 }}>
                            <Box
                              onClick={() => setSelectedVideoId(video.youtube_id!)}
                              sx={{
                                position: 'relative',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                cursor: 'pointer',
                                transition: 'transform 0.2s ease-in-out',
                                '&:hover': {
                                  transform: 'translateY(-4px)',
                                  '& .overlay': { opacity: 1 },
                                },
                              }}
                            >
                              <Box
                                component="img"
                                src={`https://img.youtube.com/vi/${video.youtube_id}/hqdefault.jpg`}
                                alt={video.title || '動画'}
                                sx={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.1)' }}
                              />
                              <Box className="overlay" sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.3)', opacity: 0, transition: 'opacity 0.2s ease-in-out', zIndex: 1 }} />
                              <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', backgroundColor: 'rgba(0, 0, 0, 0.3)', borderRadius: '50%', p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
                                <PlayArrowIcon sx={{ fontSize: { xs: 28, md: 40 }, color: '#fff' }} />
                              </Box>
                            </Box>
                            {video.title && (
                              <Typography variant="caption" sx={{ mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', lineHeight: 1.3 }}>
                                {video.title}
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}

              {/* Gallery (TMDB Images) - 一時的にスーパーユーザーのみ */}
              {user?.is_superuser && anime.tmdb_images && anime.tmdb_images.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <AnimeGallerySection images={anime.tmdb_images} />
                </Box>
              )}

              {/* 評価スコア */}
              <AnimeScoresSection
                malScore={scoreNum}
                malScoredBy={anime.scored_by ?? null}
                tmdbVoteAverage={anime.tmdb_detail?.vote_average ?? null}
                tmdbVoteCount={anime.tmdb_detail?.vote_count ?? null}
              />

              {/* Characters & Voice Actors */}
              {displayCharacters.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <SectionTitle>キャラクター・声優</SectionTitle>
                  <Grid container spacing={1}>
                    {displayCharacters.map((char) => (
                      <Grid item xs={3} sm={2.4} md={2} key={char.character.character_id}>
                        <Box sx={{
                          borderRadius: '6px',
                          bgcolor: 'rgba(255, 255, 255, 0.02)',
                          border: '1px solid rgba(255, 255, 255, 0.05)',
                          overflow: 'hidden',
                        }}>
                          <Box component="img"
                            src={char.character.image_url || '/image/no_image.png'}
                            alt={char.character.name}
                            sx={{ width: '100%', aspectRatio: '3/4', objectFit: 'cover', display: 'block' }}
                          />
                          <Box sx={{ p: 0.75 }}>
                            <Typography noWrap sx={{ fontSize: '0.65rem', fontWeight: 500, color: '#ffffffcc' }}>
                              {char.character.name_kanji || char.character.name}
                            </Typography>
                            <Typography noWrap sx={{ fontSize: '0.6rem', color: '#a7a7a7' }}>
                              {char.voice_actors.join(', ')}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                  {(charHasHidden || (charExpanded && charHasMore)) && (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      <Button
                        onClick={charHasHidden ? () => setCharExpanded(true) : loadMoreCharacters}
                        disabled={charLoading}
                        variant="outlined"
                        size="small"
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.7rem',
                          color: '#ffffffcc',
                          borderColor: 'rgba(255, 255, 255, 0.15)',
                          '&:hover': { borderColor: 'rgba(255, 255, 255, 0.3)', bgcolor: 'rgba(255, 255, 255, 0.04)' },
                        }}
                      >
                        {charLoading ? <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} /> : null}
                        もっと見る ({anime.character_voice_actors_count - displayCharacters.length})
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              {/* Staff */}
              {allStaff.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <SectionTitle>スタッフ</SectionTitle>
                  <Grid container spacing={1}>
                    {allStaff.map((s) => (
                      <Grid item xs={12} sm={6} md={4} key={s.person.person_id}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: 0.75,
                          borderRadius: '4px',
                          bgcolor: 'rgba(255, 255, 255, 0.02)',
                        }}>
                          <Box component="img"
                            src={s.person.image_url || '/image/no_image.png'}
                            alt={s.person.name}
                            sx={{ width: 32, height: 44, objectFit: 'cover', borderRadius: '3px', flexShrink: 0 }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography noWrap sx={{ fontSize: '0.65rem', fontWeight: 500, color: '#ffffffcc' }}>
                              {s.person.given_name && s.person.family_name
                                ? `${s.person.family_name} ${s.person.given_name}`
                                : s.person.name
                              }
                            </Typography>
                            <Typography sx={{ fontSize: '0.6rem', color: '#a7a7a7' }}>
                              {s.positions.join(', ')}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                  {(staffHasHidden || (staffExpanded && staffHasMore)) && (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      <Button
                        onClick={staffHasHidden ? () => setStaffExpanded(true) : loadMoreStaff}
                        disabled={staffLoading}
                        variant="outlined"
                        size="small"
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.7rem',
                          color: '#ffffffcc',
                          borderColor: 'rgba(255, 255, 255, 0.15)',
                          '&:hover': { borderColor: 'rgba(255, 255, 255, 0.3)', bgcolor: 'rgba(255, 255, 255, 0.04)' },
                        }}
                      >
                        {staffLoading ? <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} /> : null}
                        もっと見る ({anime.staff_count - allStaff.length})
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              {/* Relations (前作・続編のみ、DBに存在するもののみ) */}
              {anime.relations.filter(r => (r.relation === 'Sequel' || r.relation === 'Prequel') && r.in_db).length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <SectionTitle>関連作品</SectionTitle>
                  {Object.entries(
                    anime.relations
                      .filter(r => (r.relation === 'Sequel' || r.relation === 'Prequel') && r.in_db)
                      .reduce<Record<string, AnimeRelation[]>>((acc, rel) => {
                        const key = rel.relation;
                        if (!acc[key]) acc[key] = [];
                        acc[key].push(rel);
                        return acc;
                      }, {})
                  ).map(([relation, items]) => (
                    <Box key={relation} sx={{ mb: 2 }}>
                      <Typography variant="caption" sx={{ color: '#a7a7a7', fontSize: '0.65rem', mb: 0.5, display: 'block' }}>
                        {relationLabelMap[relation] || relation}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {items.map((rel) => (
                          <Box
                            key={`${rel.related_mal_id}-${rel.relation}`}
                            component={rel.related_type === 'anime' ? Link : 'div'}
                            {...(rel.related_type === 'anime' ? { to: `/anime/${rel.related_mal_id}` } : {})}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              p: 1,
                              borderRadius: '6px',
                              bgcolor: 'rgba(255, 255, 255, 0.03)',
                              border: '1px solid rgba(255, 255, 255, 0.06)',
                              transition: 'all 0.2s ease',
                              textDecoration: 'none',
                              color: rel.related_type === 'anime' ? '#ffffffcc' : '#a7a7a7',
                              cursor: rel.related_type === 'anime' ? 'pointer' : 'default',
                              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.06)', color: rel.related_type === 'anime' ? '#ffffff' : undefined },
                              fontSize: '0.7rem',
                            }}
                          >
                            {rel.related_title_japanese || rel.related_name || `MAL ID: ${rel.related_mal_id}`}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>

            {/* Right Column - Stats Sidebar */}
            <Grid item xs={12} md={4}>
              <Box sx={{ position: 'sticky', top: '80px' }}>
                {/* Stats Card */}
                <Box sx={{
                  p: 2,
                  borderRadius: '8px',
                  bgcolor: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                  mb: 2,
                }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, mb: 1.5, color: '#ffffffd9' }}>
                    統計 - global
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {/* {scoreNum && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant='body2' sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>スコア</Typography>
                        <Typography variant='body2' sx={{ fontSize: '0.65rem', color: getScoreColor(scoreNum), fontWeight: 600 }}>
                          {scoreNum.toFixed(2)}
                        </Typography>
                      </Box>
                    )} */}
                    {anime.rank && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant='body2' sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>評価ランク</Typography>
                        <Typography variant='body2' sx={{ fontSize: '0.65rem', color: 'text.primary' }}>#{anime.rank}</Typography>
                      </Box>
                    )}
                    {anime.popularity && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant='body2' sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>人気度</Typography>
                        <Typography variant='body2' sx={{ fontSize: '0.65rem', color: 'text.primary' }}>#{anime.popularity}</Typography>
                      </Box>
                    )}
                    {/* {anime.members !== undefined && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant='body2' sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>コミュニティ規模</Typography>
                        <Typography variant='body2' sx={{ fontSize: '0.65rem', color: 'text.primary' }}>{anime.members.toLocaleString()}</Typography>
                      </Box>
                    )}
                    {anime.favorites !== undefined && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant='body2' sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>お気に入り</Typography>
                        <Typography variant='body2' sx={{ fontSize: '0.65rem', color: 'text.primary' }}>{anime.favorites.toLocaleString()}</Typography>
                      </Box>
                    )} */}
                  </Box>
                </Box>

                {/* Status Counts */}
                {anime.status_counts && anime.status_counts.total > 0 && (
                  <Box sx={{
                    p: 2,
                    borderRadius: '8px',
                    bgcolor: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                  }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, mb: 1.5, color: '#ffffffd9' }}>
                      ユーザーステータス
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {(['s1', 's2', 's3', 's4', 's5'] as const).map((key) => {
                        const count = anime.status_counts![key];
                        const total = anime.status_counts!.total;
                        const pct = total > 0 ? (count / total) * 100 : 0;
                        return (
                          <Box key={key}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                              <Typography sx={{ fontSize: '0.6rem', color: '#a7a7a7' }}>{getStatusLabel(key)}</Typography>
                              <Typography sx={{ fontSize: '0.6rem', color: '#ffffffcc' }}>{count}</Typography>
                            </Box>
                            <Box sx={{ width: '100%', height: 3, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 1 }}>
                              <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: 'rgba(255,255,255,0.3)', borderRadius: 1 }} />
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {/* 外部リンク */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                  {anime.tmdb_detail?.homepage && (
                    <Button
                      variant="contained"
                      size="small"
                      fullWidth
                      component="a"
                      href={anime.tmdb_detail.homepage}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.06)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        textTransform: 'none',
                        fontSize: '0.7rem',
                        color: '#ffffffe8',
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.12)' }
                      }}
                    >
                      公式サイト
                    </Button>
                  )}
                  {anime.external_ids?.twitter_id && (
                    <Button
                      variant="contained"
                      size="small"
                      fullWidth
                      component="a"
                      href={`https://x.com/${anime.external_ids.twitter_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.06)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        textTransform: 'none',
                        fontSize: '0.7rem',
                        color: '#ffffffe8',
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.12)' }
                      }}
                    >
                      X
                    </Button>
                  )}
                  {user?.is_superuser && (
                    <Button
                      variant="contained"
                      size="small"
                      fullWidth
                      component="a"
                      href={`https://myanimelist.net/anime/${anime.mal_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.06)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        textTransform: 'none',
                        fontSize: '0.7rem',
                        color: '#ffffffe8',
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.12)' }
                      }}
                    >
                      MyAnimeList
                    </Button>
                  )}
                  {user?.is_superuser && anime.tmdb_id && (
                    <Button
                      variant="contained"
                      size="small"
                      fullWidth
                      component="a"
                      href={`https://www.themoviedb.org/${anime.type === 'Movie' ? 'movie' : 'tv'}/${anime.tmdb_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.06)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        textTransform: 'none',
                        fontSize: '0.7rem',
                        color: '#ffffffe8',
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.12)' }
                      }}
                    >
                      TMDB
                    </Button>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </NormalSection>

      {/* Wikipedia Viewer */}
      <WikipediaViewer
        open={wikiOpen}
        onClose={() => setWikiOpen(false)}
        title={anime.title_japanese || anime.title}
        gameId={anime.mal_id}
      />

      {/* Youtube Viewer */}
      <YoutubeViewer
        open={!!selectedVideoId}
        onClose={() => setSelectedVideoId(null)}
        videoId={selectedVideoId || ''}
      />

      {/* Review Dialog */}
      {anime && (
        <AnimeReviewDialog
          open={openReviewDialog}
          onClose={() => setOpenReviewDialog(false)}
          malId={anime.mal_id}
          initialData={reviewData}
          animeType={anime.type}
          totalEpisodes={anime.episodes}
          airedFrom={anime.aired_from}
          onReviewSaved={(newData) => setReviewData(newData)}
        />
      )}
    </>
  );
};

export default memo(AnimeDetailsPage);
